import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StickyHeaderLayout } from '@components/StickyHeaderLayout';
import { ClaimEdit, ClaimLineageBanner } from '@features/billing/components/Claim';
import { useGetClaimByIdQuery, useLazyGetClaimPdfQuery, useLazyGetStediPdfQuery } from '@features/billing/store';
import { downloadBase64Pdf } from '@features/billing/utils';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Container,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';

export const ClaimView: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { claimId } = useParams<{ claimId: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const { data: claim } = useGetClaimByIdQuery(
    { claimId: claimId || '' },
    { skip: !claimId }
  );

  const [getClaimPdf, { isLoading: isDownloadingLegacy }] = useLazyGetClaimPdfQuery();
  const [getStediPdf, { isLoading: isDownloadingStedi }] = useLazyGetStediPdfQuery();
  const isDownloading = isDownloadingLegacy || isDownloadingStedi;

  // Split button state
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleDownload = async (background: boolean) => {
    if (!claimId) return;
    setMenuOpen(false);

    try {
      let result;
      try {
        result = await getStediPdf({ claimId, background }).unwrap();
      } catch (stediError) {
        console.warn('Stedi PDF failed, falling back to legacy endpoint:', stediError);
        result = await getClaimPdf({ claimId }).unwrap();
      }

      const filename = result.filename && !result.filename.includes('None')
        ? result.filename
        : `claim_${claimId}.pdf`;

      downloadBase64Pdf(result.pdf_data, filename);
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to download PDF:', error);
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
  };

  const hasBeenSubmitted = ['submitted', 'acknowledged', 'accepted', 'rejected'].includes(claim?.status || '');

  const headerActions = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
      {hasBeenSubmitted && (
        <ButtonGroup variant="contained" ref={anchorRef} disabled={isDownloading}>
          <Button
            startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={() => handleDownload(true)}
          >
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Button
            size="small"
            onClick={() => setMenuOpen(prev => !prev)}
            sx={{ px: 0.5, minWidth: 'auto' }}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
      )}

      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        transition
        placement="bottom-end"
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={4} sx={{ minWidth: 280 }}>
              <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                <MenuList>
                  <MenuItem onClick={() => handleDownload(true)} sx={{ py: 1.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        With background
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        CMS-1500 form overlay in red ink. Print on blank paper.
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => handleDownload(false)} sx={{ py: 1.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Without background
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Data only. Print onto pre-printed CMS-1500 forms.
                      </Typography>
                    </Box>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Stack>
  );

  return (
    <Box sx={{ minHeight: '100%', backgroundColor: theme.palette.background.default }}>
      <StickyHeaderLayout
        title="View Claim"
        subtitle="CMS-1500 Health Insurance Claim Form (Read-Only)"
        backButton={{
          label: 'Back to Claims',
          onClick: () => navigate('/billing?tab=claims'),
        }}
        actions={headerActions}
        containerId="claim-view"
      >
        <Container maxWidth="xl" sx={{ pb: 3 }}>
          {claimId && <ClaimLineageBanner claimId={claimId} />}
          <ClaimEdit readOnly />
        </Container>
      </StickyHeaderLayout>
    </Box>
  );
};
